import type {ConversationMessage, MessageContent} from "@/services/wingman/types";

/** Combine approval continuations for display without changing persisted message IDs. */
type DisplayMessage = ConversationMessage & {approvalMessageIds: Record<string, string>};

function approvalMessageIds(message: ConversationMessage): Record<string, string> {
   return Object.fromEntries(message.contents.flatMap(content =>
      content.type === "function_call" && content.approval ? [[content.approval.id, message.id]] : []));
}

function isApprovalContinuation(previous: DisplayMessage, message: ConversationMessage): boolean {
   return previous.role === "assistant" && !previous.synthetic &&
      message.role === "assistant" && !message.synthetic &&
      previous.contents.some(content => content.type === "function_call" && content.approval);
}

function existingContentIndex(contents: MessageContent[], content: MessageContent): number {
   if (content.type !== "function_call" && content.type !== "function_result") return -1;
   return contents.findIndex(item => item.type === content.type && "callId" in item && item.callId === content.callId);
}

function mergeContent(existing: MessageContent, incoming: MessageContent): MessageContent {
   if (incoming.type !== "function_call" || existing.type !== "function_call") return incoming;
   const approval = incoming.approval
      ? {...existing.approval, ...incoming.approval, proposal: incoming.approval.proposal ?? existing.approval?.proposal}
      : existing.approval;
   return {...incoming, approval};
}

function mergeContinuation(previous: DisplayMessage, message: ConversationMessage, ids: Record<string, string>): DisplayMessage {
   const contents = [...previous.contents];
   for (const content of message.contents) {
      const index = existingContentIndex(contents, content);
      if (index < 0) contents.push(content);
      else contents[index] = mergeContent(contents[index], content);
   }
   // The latest server ID owns feedback, streaming state, and any new approval.
   return {
      ...message,
      contents,
      approvalMessageIds: {...previous.approvalMessageIds, ...ids},
      recalledFacts: [...new Set([...(previous.recalledFacts ?? []), ...(message.recalledFacts ?? [])])],
   };
}

export function groupApprovalContinuations(messages: ConversationMessage[]): DisplayMessage[] {
   const grouped: DisplayMessage[] = [];
   for (const message of messages) {
      const ids = approvalMessageIds(message);
      const previous = grouped.at(-1);
      if (!previous || !isApprovalContinuation(previous, message)) {
         grouped.push({...message, approvalMessageIds: ids});
         continue;
      }
      grouped[grouped.length - 1] = mergeContinuation(previous, message, ids);
   }
   return grouped;
}
